import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShoppingBag } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const success = await addToCart(product.id, null, 1);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  return (
    <Link 
      to={`/product/${product.id}`}
      className="group product-card bg-white rounded-2xl overflow-hidden border border-border/50"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="aspect-square overflow-hidden bg-muted relative">
        <img 
          src={product.image_url} 
          alt={product.name}
          className="w-full h-full object-cover img-zoom"
        />
        {/* Quick Add Button */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            size="icon"
            className="bg-[#9333EA] hover:bg-[#7C3AED] rounded-full h-10 w-10 shadow-lg"
            onClick={handleQuickAdd}
            disabled={loading}
            data-testid={`quick-add-${product.id}`}
          >
            <ShoppingBag className="h-5 w-5" />
          </Button>
        </div>
        {/* Fabric variants indicator */}
        {product.fabric_variants && product.fabric_variants.length > 0 && (
          <div className="absolute top-3 left-3">
            <span className="bg-white/90 backdrop-blur-sm text-pockett-gray text-xs font-medium px-2 py-1 rounded-full">
              {product.fabric_variants.length} prints
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              {product.category}
            </p>
            <h3 className="font-heading font-semibold text-[#9333EA] group-hover:text-[#7C3AED] transition-colors">
              {product.name}
            </h3>
          </div>
          <span className="font-heading font-bold text-[#9333EA]">
            ${product.price.toFixed(2)}
          </span>
        </div>
        
        {/* Variant swatches preview */}
        {product.fabric_variants && product.fabric_variants.length > 0 && (
          <div className="flex gap-1 mt-3">
            {product.fabric_variants.slice(0, 4).map((variant) => (
              <div
                key={variant.id}
                className="w-6 h-6 rounded-full overflow-hidden border-2 border-white shadow-sm"
                title={variant.name}
              >
                <img 
                  src={variant.image_url} 
                  alt={variant.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {product.fabric_variants.length > 4 && (
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                +{product.fabric_variants.length - 4}
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
